import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import SeverityOfAlertsApexChart from "../../../Charts/Mixed/SeverityOfAlertsApexChart";
import { getSmartAlertsTimeseries } from "../../../Api/SmartAlertsAnalyticsApi";
import { calculateGranularity } from "../../BmsFaults/CommonFunctions";
import { formatDateLabel } from "../../../Helper/Analytics/AnalyticsHelper";
import CODE from "../../../Static/Constants/StatusCodes";

const CHART_HEIGHT = 315;
const EMPTY_FILTERS = [];
const EMPTY_SEARCH_TERM = { selectedDevices: [], selectedSearchColumn: null };

const SeverityOfAlertsChartView = ({
  isSidebarExpanded = false,
  startDate,
  endDate,
  granularity,
  globalFilters = {},
  filters = EMPTY_FILTERS,
  searchTerm = EMPTY_SEARCH_TERM,
  onBarClick,
}) => {
  const { baseEntityTypePluralLable } = useBaseEntityInfo();
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({
    categories: [],
    dateRanges: [],
    warningData: [],
    highSeverityData: [],
    criticalData: [],
    vehiclesAffectedData: [],
  });

  useEffect(() => {
    if (!startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);

    const resolvedGranularity = (granularity || calculateGranularity(startDate, endDate)).toUpperCase();

    const searchFilters = searchTerm?.selectedDevices?.length > 0 && searchTerm?.selectedSearchColumn
      ? [{ name: searchTerm.selectedSearchColumn, values: searchTerm.selectedDevices }]
      : [];
    const combinedFilters = [...(filters || []), ...searchFilters];

    Promise.all([
      getSmartAlertsTimeseries(globalFilters, {
        startDate,
        endDate,
        select: ["GeneratedAlertsCount"],
        groupBy: ["AlertSeverityName"],
        granularity: resolvedGranularity,
        ...(combinedFilters.length ? { filters: combinedFilters } : {}),
      }),
      getSmartAlertsTimeseries(globalFilters, {
        startDate,
        endDate,
        select: ["VehiclesAffectedCountForGeneratedAlerts"],
        granularity: resolvedGranularity,
        ...(combinedFilters.length ? { filters: combinedFilters } : {}),
      }),
    ]).then(([severityRes, vehiclesRes]) => {
      if (cancelled) return;

      const severityData = severityRes?.responseStatus?.code === CODE.SUCCESS
        ? severityRes.response?.data || []
        : [];
      const vehiclesData = vehiclesRes?.responseStatus?.code === CODE.SUCCESS
        ? vehiclesRes.response?.data || []
        : [];

      const dateRanges        = severityData.map((item) => item.xLabel);
      const categories        = severityData.map((item) => formatDateLabel(item.xLabel));
      const warningData       = [];
      const highSeverityData  = [];
      const criticalData      = [];

      severityData.forEach((item) => {
        const metrics = item.metricsArray || [];
        const getVal = (type) => metrics.find((m) => m.type === type)?.value ?? 0;
        warningData.push(getVal("Warning") || getVal("Medium") || getVal("Low") || 0);
        highSeverityData.push(getVal("High Severity") || getVal("High") || 0);
        criticalData.push(getVal("Critical"));
      });

      // Build xLabel → vehicles map for robust alignment
      const vehiclesMap = {};
      vehiclesData.forEach((item) => {
        const metrics = item.metricsArray || [];
        vehiclesMap[item.xLabel] = metrics.find((m) => m.type === "VehiclesAffectedCountForGeneratedAlerts")?.value ?? 0;
      });
      const vehiclesAffectedData = severityData.map((item) => vehiclesMap[item.xLabel] ?? 0);

      setChartData({ categories, dateRanges, warningData, highSeverityData, criticalData, vehiclesAffectedData });
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [startDate, endDate, granularity, globalFilters, filters, searchTerm]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: CHART_HEIGHT + 50 }}>
        <CartLoader />
      </Box>
    );
  }

  return (
    <SeverityOfAlertsApexChart
      chartData={chartData}
      isSidebarExpanded={isSidebarExpanded}
      onBarClick={onBarClick}
      baseEntityTypePluralLable={baseEntityTypePluralLable}
    />
  );
};

export default SeverityOfAlertsChartView;
